import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import PasswordValidator from 'password-validator';
import { MainButton } from '../buttons/MainButton';
import TextInputForm from '../textInputForm/TextInputForm';
import theme from '../../constants/theme.style.js';
import CustomText from '../customText/CustomText';

const styles = StyleSheet.create({
  container: {
    display: 'flex',
  },
  headline: {
    fontSize: theme.FONT_SIZE_TITLE,
    color: theme.GREEN_DARKER_COLOR,
    fontWeight: theme.FONT_WEIGHT_BOLD,
    marginBottom: 5,
    textAlign: 'center',
  },
  loginButton: {
    marginTop: 40,
    marginBottom: 60,
  },
  forgotPassword: {
    marginTop: 5,
    fontWeight: '300',
    alignSelf: 'center',
    fontSize: theme.FONT_SIZE_SMALL,
    marginBottom: 25,
  },
  accountQuestion: {
    fontWeight: '300',
    fontSize: theme.FONT_SIZE_SMALL,
    alignSelf: 'center',
  },
  signUpLink: {
    paddingLeft: 5,
    textDecorationLine: 'underline',
    color: theme.GREEN_WARM_COLOR,
    alignSelf: 'center',
  },
  incorrect: {
    textAlign: 'center',
    color: theme.RED_ERROR_COLOR,
  },
});

const SettingsForm = (props) => {
  const { onUpdate, onLogout, onResetAll, currentUser, nav } = props;
  const [nickname, setNickname] = useState(currentUser.user.nickname);
  const [nicknameError, setNicknameError] = useState('');
  const [mail, setMail] = useState(currentUser.user.email);
  const [mailError, setMailError] = useState('');
  const [firstName, setFirstName] = useState(currentUser.user.firstname);
  const [firstNameError, setFirstNameError] = useState('');
  const [lastName, setLastName] = useState(currentUser.user.lastname);
  const [lastNameError, setLastNameError] = useState('');

  const passwordSchema = new PasswordValidator();
  const nameSchema = new PasswordValidator();
  const nicknameSchema = new PasswordValidator();

  passwordSchema
    .is()
    .min(6)
    .is()
    .max(20)
    .has()
    .uppercase()
    .has()
    .lowercase()
    .has()
    .digits()
    .has()
    .not()
    .spaces();

  nameSchema.is().min(2).is().max(20).has().not().digits().has().uppercase();

  nicknameSchema.is().min(3).is().max(15).has().not().spaces();

  function updateUser() {
    setNicknameError('');
    setMailError('');
    setFirstNameError('');
    setLastNameError('');

    let error = false;

    // nickname
    const nicknameValidation = nicknameSchema.validate(nickname, { list: true });
    if (nicknameValidation.length > 0) {
      error = true;
      let errorNickname = '';
      nicknameValidation.forEach((validationError) => {
        switch (validationError) {
          case 'min':
            errorNickname += 'Must be at least 2 characters long\n';
            break;
          case 'max':
            errorNickname += 'Must be max 20 characters long \n';
            break;
          case 'spaces':
            errorNickname += 'Must not contain spaces \n';
            break;
          default:
            break;
        }
      });
      setNicknameError(errorNickname);
    }
    if (
      !/^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        mail,
      )
    ) {
      error += 1;
      setMailError('Enter a valid mail adress.');
    }

    // first name
    const firstNameValidation = nameSchema.validate(firstName, { list: true });
    if (firstNameValidation.length > 0) {
      error = true;
      let errorFirstName = '';
      firstNameValidation.forEach((validationError) => {
        switch (validationError) {
          case 'min':
            errorFirstName += 'Must be at least 2 characters long\n';
            break;
          case 'max':
            errorFirstName += 'Must be max 20 characters long \n';
            break;
          case 'digits':
            errorFirstName += 'Must not contain digits \n';
            break;
          case 'uppercase':
            errorFirstName += 'Must start with an uppercase letter \n';
            break;
          default:
            break;
        }
      });
      setFirstNameError(errorFirstName);
    }
    // last name
    const lastNameValidation = nameSchema.validate(lastName, { list: true });
    if (lastNameValidation.length > 0) {
      error = true;
      let errorLastName = '';
      lastNameValidation.forEach((validationError) => {
        switch (validationError) {
          case 'min':
            errorLastName += 'Must be at least 2 characters long\n';
            break;
          case 'max':
            errorLastName += 'Must be max 20 characters long \n';
            break;
          case 'digits':
            errorLastName += 'Must not contain digits \n';
            break;
          case 'uppercase':
            errorLastName += 'Must start with an uppercase letter \n';
            break;
          default:
            break;
        }
      });
      setLastNameError(errorLastName);
    }
    if (!error) {
      onUpdate({
        id: currentUser.user.id,
        username: nickname,
        mail,
        firstName,
        lastName,
      }).then((result) => {
        switch (result.error) {
          case '':
            nav.navigate('Profile');
            break;
          default:
        }
      });
    }
  }
  function logoutUser() {
    onLogout()
      .then(() => nav.navigate('Auth'))
      .then(() => onResetAll());
  }

  return (
    <View style={styles.container}>
      <TextInputForm headline="Email" text={mail} callback={setMail} />
      {mailError !== '' && (
        <CustomText style={styles.incorrect} testID="mailError">
          {mailError}
        </CustomText>
      )}
      <TextInputForm headline="Nickname" text={nickname} callback={setNickname} />
      {nicknameError !== '' && (
        <CustomText style={styles.incorrect} testID="nickNameError">
          {nicknameError}
        </CustomText>
      )}
      <TextInputForm headline="First Name" text={firstName} callback={setFirstName} />
      {firstNameError !== '' && (
        <CustomText style={styles.incorrect} testID="firstNameError">
          {firstNameError}
        </CustomText>
      )}
      <TextInputForm
        headline="Last Name"
        testID="lastname"
        text={lastName}
        callback={setLastName}
      />
      {lastNameError !== '' && (
        <CustomText style={styles.incorrect} testID="lastNameError">
          {lastNameError}
        </CustomText>
      )}
      <MainButton
        title="Save"
        btnBgColor={theme.GREEN_DARKER_COLOR}
        textColor="#FFFFFF"
        btnTextSize={15}
        btnWidth={200}
        btnMarginTop={theme.CONTAINER_MARGIN}
        btnPadding="5%"
        btnPress={() => updateUser()}
        style={styles.loginButton}
      />
      <MainButton
        title="Logout"
        btnBgColor={theme.GREY_MEDIUM_COLOR}
        textColor="#FFFFFF"
        btnTextSize={15}
        btnWidth={200}
        btnPadding="5%"
        btnMarginTop={theme.CONTAINER_MARGIN}
        btnPress={() => {
          logoutUser();
        }}
        style={styles.loginButton}
      />
    </View>
  );
};

export default SettingsForm;
